import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Scale, UserCheck, MessageSquare, AlertTriangle, Mail, FileText, MapPin } from "lucide-react";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-primary-light rounded-full flex items-center justify-center mx-auto mb-6">
            <Scale className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl font-bold text-secondary mb-4">Terms of Service</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Please read these terms carefully before using our services.
          </p>
        </div>

        {/* Effective Date */}
        <div className="mb-8">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="py-4">
              <p className="text-green-800 font-medium text-center">
                <strong>Effective Date:</strong> July 14, 2025
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="space-y-8">
          {/* Introduction */}
          <Card>
            <CardContent className="py-6">
              <p className="text-gray-700 leading-relaxed">
                Welcome to UdyogaPramoda.org. These Terms of Service ("Terms") govern your use of our services, 
                including websites, training platforms, and mentor matching programs.
              </p>
            </CardContent>
          </Card>

          {/* 1. Eligibility */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-xl font-bold text-secondary">
                <UserCheck className="w-6 h-6 mr-3 text-primary" />
                1. Eligibility
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                By accessing or using our services, you confirm that you are at least 16 years old and 
                legally able to agree to these terms.
              </p>
            </CardContent>
          </Card>

          {/* 2. Use of Services */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-xl font-bold text-secondary">
                <FileText className="w-6 h-6 mr-3 text-primary" />
                2. Use of Services
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-700">
                You agree to use our services only for lawful purposes and in a manner consistent with 
                our mission to help aspirants find jobs and grow professionally.
              </p>
              <div>
                <h4 className="font-semibold text-secondary mb-2">You agree not to:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    Impersonate another person or provide false information
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    Harass, abuse, or harm other users (including mentors, aspirants, or staff)
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    Violate any applicable law
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* 3. User Accounts */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-xl font-bold text-secondary">
                <UserCheck className="w-6 h-6 mr-3 text-purple-600" />
                3. User Accounts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                You may need to register for an account. You are responsible for safeguarding your login 
                credentials and for all activity under your account.
              </p>
            </CardContent>
          </Card>

          {/* 4. Content You Provide */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-xl font-bold text-secondary">
                <MessageSquare className="w-6 h-6 mr-3 text-indigo-600" />
                4. Content You Provide
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                You grant us a limited license to use any content you provide (e.g., profile information, 
                messages, resumes) solely to deliver our services. You retain ownership of your content.
              </p>
            </CardContent>
          </Card>

          {/* 5. Mentor and Training Interactions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-bold text-secondary">
                5. Mentor and Training Interactions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                While we take care to vet mentors and training providers, we are not responsible for the 
                accuracy of their advice, nor do we guarantee job placement.
              </p>
            </CardContent>
          </Card>

          {/* 6. Limitation of Liability */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-xl font-bold text-secondary">
                <AlertTriangle className="w-6 h-6 mr-3 text-orange-600" />
                6. Limitation of Liability
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-orange-900">
                  <strong>Important:</strong> To the extent permitted by law, we are not liable for any 
                  indirect, incidental, or consequential damages arising from your use of our services.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 7. Termination */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-bold text-secondary">
                7. Termination
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                We reserve the right to suspend or terminate your access if you violate these Terms or 
                engage in harmful behavior.
              </p>
            </CardContent>
          </Card>

          {/* 8. Changes to Terms */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-bold text-secondary">
                8. Changes to Terms
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                We may update these Terms at any time. Continued use of our services after updates means 
                you accept the new Terms.
              </p>
            </CardContent>
          </Card>

          {/* 9. Governing Law */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-xl font-bold text-secondary">
                <MapPin className="w-6 h-6 mr-3 text-primary" />
                9. Governing Law
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                These Terms are governed by the laws of Hyderabad, Telangana State, India.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Contact Information */}
        <div className="mt-12">
          <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
            <CardContent className="py-8 text-center">
              <Mail className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold text-secondary mb-2">
                Questions About Our Terms?
              </h3>
              <p className="text-gray-600 mb-4">
                If you have any questions about these Terms of Service, please don't hesitate to contact us.
              </p>
              <a 
                href="mailto:info@udyogapramoda.org?subject=Terms%20of%20Service%20Question"
                className="inline-flex items-center px-6 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors"
              >
                <Mail className="w-5 h-5 mr-2" />
                Contact Us
              </a>
            </CardContent>
          </Card>
        </div>

        {/* Acceptance Notice */}
        <div className="mt-8">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="py-6 text-center">
              <p className="text-blue-800">
                <strong>By using UdyogaPramoda.org, you acknowledge that you have read, understood, 
                and agree to be bound by these Terms of Service.</strong>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
} 